import React, { useContext, useEffect, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import { Button, Flex, Form, InputNumber, message, Table } from 'antd';
import Title from 'antd/lib/typography/Title';
import { ColumnsType } from 'antd/lib/table';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { EditFilled, CheckOutlined } from '@ant-design/icons';

import { ICategoryRevenue, IRevenueByYear } from '~/data/revenue';
import { TITLE_LEVEL } from '~/utils/constants';
import { formatPercent, formatPrice, getErrorMessageByCode } from '~/utils/helper';
import { selectClientDetail } from '~/thunks/clientDetail/clientDetailSlice';
import styles from './RevenueTable.module.scss';
import { EditableCellRevenueProps } from '~/pages/clients/clientDetails/clientDetailsProducts/ClientDetailsProducts.interface';
import { LoadingContext } from '~/contexts/LoadingContext';
import { getClientDetail, updateRevenue } from '~/thunks/clientDetail/clientDetailThunk';
import { IPayloadUpdateRevenueClient } from '~/pages/clients/Clients.interface';

const cx = classNames.bind(styles);

type ColumnTypes<T> = ColumnsType<T>;

type CategoryKey = keyof Omit<IRevenueByYear, 'year'>;

const EditableCell: React.FC<EditableCellRevenueProps> = (props) => {
  const { editable, children, dataIndex, record, handleSave, ...restProps } = props;
  const [editing, setEditing] = useState(false);
  const [changeLogic, setChangeLogic] = useState(false);
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const inputRef = useRef<any>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    if (!editing && !changeLogic) {
      form?.setFieldsValue({ total: record?.total });
    }
    setEditing(!editing);
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      record && handleSave({ ...record, ...values });
      inputRef.current!.focus();
      setChangeLogic(false);
    } catch (errInfo) {
      toggleEdit();
      console.log('Save failed:', errInfo);
    }
  };

  const handleFocusInput = async () => {
    try {
      toggleEdit();
      setChangeLogic((prev) => !prev);
    } catch (errInfo) {
      console.log('Save failed:', errInfo);
    }
  };

  const generatePercentLabel = (percent: number) => {
    const percentNumber = Number(percent);
    if (!percentNumber) return;

    if (percentNumber > 0) {
      return `+${formatPercent(percentNumber)}%`;
    }

    if (percentNumber < 0) {
      return `${formatPercent(percentNumber)}%`;
    }
  };

  const generatePercentClassName = (percent: number) => {
    const percentNumber = Number(percent);
    if (!percentNumber) return;

    if (percentNumber > 0) {
      return 'increase';
    }

    if (percentNumber < 0) {
      return 'decrease';
    }
  };

  let childNode = children;

  if (editable) {
    childNode = (
      <Form form={form} component={false}>
        <Form.Item
          className={cx('clientDetailProductsEditCell')}
          name='total'
          rules={[
            {
              validator: (_, value) => {
                if (value === undefined || value === null) return Promise.resolve();
                if (isNaN(value)) return Promise.reject(t('clients_clientDetails_products_subCategory_discount_validate_invalid_number'));
                return Promise.resolve();
              },
            },
          ]}
        >
          <Flex align='center'>
            {editing ? (
              <Button className={cx('tableIconEdit')} type='text' shape='circle' icon={<CheckOutlined />} onClick={handleFocusInput} />
            ) : (
              <Button className={cx('tableIconEdit')} type='text' shape='circle' icon={<EditFilled />} onClick={handleFocusInput} />
            )}

            {editing ? (
              <InputNumber controls={false} ref={inputRef} className={'editTableCellInputNumber'} value={record?.total} onPressEnter={save} onBlur={save} />
            ) : (
              <>
                <span>{formatPrice(record?.total ?? 0)}</span>
                <strong className={cx('revenue-percent', generatePercentClassName(record?.percent ?? 0))}>{generatePercentLabel(record?.percent ?? 0)}</strong>
              </>
            )}
          </Flex>
        </Form.Item>
      </Form>
    );
  }
  return <td {...restProps}>{childNode}</td>;
};

const RevenueTable = () => {
  const { t } = useTranslation();
  const loadingContext = useContext(LoadingContext);
  const dispatch = useAppDispatch();

  const clientDetail = useAppSelector(selectClientDetail);

  const [revenue, setRevenue] = useState<IRevenueByYear[]>([]);

  useEffect(() => {
    if (clientDetail?.revenue) {
      setRevenue(clientDetail.revenue);
    }
  }, [clientDetail.revenue]);

  const components = {
    body: {
      cell: EditableCell,
    },
  };

  const defaultColumns: (ColumnTypes<IRevenueByYear>[number] & {
    editable?: boolean;
    dataIndex: keyof IRevenueByYear;
  })[] = [
    {
      title: t('clients_clientDetails_year_title'),
      dataIndex: 'year',
    },
    {
      title: 'AUSTRALIAN GOLD',
      dataIndex: 'c_12',
      editable: true,
    },
    {
      title: 'RAYSISTANT',
      dataIndex: 'c_10',
      editable: true,
    },
    {
      title: 'BAIJA',
      dataIndex: 'c_13',
      editable: true,
    },
    {
      title: 'ROSALIQUE',
      dataIndex: 'c_09',
      editable: true,
    },
    {
      title: 'STRAVAC & TECNOLOGY',
      dataIndex: 'c_16',
      editable: true,
    },
  ];

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: IRevenueByYear): EditableCellRevenueProps => {
        const revenueRecord = col.dataIndex === 'year' ? undefined : record[col.dataIndex as CategoryKey];
        return {
          record: revenueRecord,
          editable: col.editable,
          dataIndex: col.dataIndex,
          handleSave: (newRevenue) => handleSave(newRevenue, record),
        };
      },
    };
  });

  const handleSave = (revenueRecord: ICategoryRevenue, record: IRevenueByYear) => {
    loadingContext?.show();

    const payload: IPayloadUpdateRevenueClient = {
      id: clientDetail.id,
      categoryId: +revenueRecord.categoryId,
      total: Number(revenueRecord.total),
      year: record.year,
    };

    dispatch(updateRevenue(payload))
      .unwrap()
      .then((_) => {
        dispatch(getClientDetail({ id: clientDetail.id }));
        loadingContext?.hide();
        message.success(t('revenueTable_update_success'));
      })
      .catch((error) => {
        loadingContext?.hide();
        getErrorMessageByCode(error.response.data.code);
      });

    setRevenue((prev) => {
      return prev.map((row) => {
        if (+row.year !== +record.year) return row;

        const categoryKey = Object.keys(row).find((key) => {
          const item = row[key as keyof IRevenueByYear] as ICategoryRevenue | number;
          return typeof item === 'object' && +item.categoryId === +revenueRecord.categoryId;
        }) as CategoryKey;

        if (!categoryKey) return row;

        return {
          ...row,
          [categoryKey]: {
            ...row[categoryKey],
            total: revenueRecord.total,
          },
        };
      });
    });
  };

  return (
    <div className={cx('revenue-wrap')}>
      <Title level={TITLE_LEVEL}>{t('clients_clientDetails_revenue_title')}</Title>
      <Table components={components} dataSource={revenue} rowKey={(record) => record.year} columns={columns} pagination={false} />
    </div>
  );
};

export default RevenueTable;
